/**
 * Anatomy as LPC paints it: deliberate marks on simple shapes, not more volumes.
 *
 * Collarbones, a lit deltoid cap, abdominals, a fist's knuckles, a knee -- modelled as bumps these
 * melt into lumps at this size, and shading alone puts a highlight wherever the light happens to
 * fall rather than one per muscle. LPC paints each muscle its own small light and shade, which is
 * what makes every joint read. So these are painted on: pixel patterns read off the LPC male body
 * (tools ember-markgen), each pinned to a joint of the skeleton so it moves with the pose.
 *
 * A mark's `at` is where LPC paints it in the standing frame of its facing; its offset from the
 * joint is measured in Ember's standing pose (default proportions), so standing still the mark lands
 * on LPC's pixel and any other pose carries it along with the joint. It paints only bare skin of the
 * parts around that joint (a shirt or an arm in front hides it), and is left off when the limb has
 * swung far from where it hung, since a pattern does not rotate.
 *
 * Front and back marks are written for the screen-left limb and mirrored; right is left mirrored.
 * Pattern characters name the skin ramp's steps (marks.json markLetters: L light, B base, S shade,
 * D deep, n line); "." leaves the render alone. The marks themselves are content
 * (`content/game/sprites/ember/marks.json` markSets); a style names the set it paints.
 */
import { bindContent } from "../content/catalog"
import { MARK_CENTERS, MARK_JOINTS, MARK_SIDES } from "./content_problems"
import { noteEmberContent } from "./model"
import { Material } from "./palette"
import { DEFAULT_RASTER, FACING_YAW, Facing, PartGroupList, PixelStamp, RasterOptions, cameraAxes, projectPoint } from "./raster"
import { DEFAULT_PROPORTIONS, LimbJoints, Proportions, Skeleton, restingSample, solvePose } from "./rig"
import { Vec3, apply, dot, mul, rotY } from "./vec"

export type MarkCenter = typeof MARK_CENTERS[number]
export type MarkJoint = typeof MARK_JOINTS[number]
/**
 * Front and back: the limb on the screen's left (a mark on the head or body given this is one of a
 * mirrored pair, like the ears); side-on: the limb nearer or farther from the camera.
 */
export type MarkSide = typeof MARK_SIDES[number]

export interface Mark {
    about?: string
    on: MarkCenter | MarkJoint
    side?: MarkSide
    /** Top-left pixel of the pattern in LPC's standing frame of this facing. */
    at: [number, number]
    rows: string[]
}

/** A set of marks (marks.json markSets): what a style paints on its bodies. */
export interface MarkSet {
    about?: string
    /** The frame the marks were read off (LPC's 64px); a bigger frame centres the same body in it. */
    frame: number
    /** Degrees a limb may swing from where it hung before its joint's marks are left off. */
    swungLimit: number
    /** How far the torso may turn from its standing turn before its marks stop fitting. */
    turnedLimit: number
    /** A facing drawn as another's marks mirrored (right is left). */
    mirrorFacings: Record<string, string>
    /** Within a facing, marks on this side also drawn mirrored on that one. */
    mirrorSides: Record<string, string>
    facings: Record<string, Mark[]>
}

export interface EmberMarkFile {
    markLetters: Record<string, string>
    markSets: Record<string, MarkSet>
}

/** The mark sets by name, filled in place when content is bound. */
export const MARK_SETS: Record<string, MarkSet> = {}
/** The skin ramp step each pattern letter paints (marks.json markLetters). */
const LETTERS: [string, keyof Material][] = []

/** Fill the mark sets from content's marks.json (bound below). */
export const registerMarks = (file: EmberMarkFile | undefined): void => {
    for (const k of Object.keys(MARK_SETS)) delete MARK_SETS[k]
    Object.assign(MARK_SETS, file?.markSets ?? {})
    LETTERS.length = 0
    for (const [letter, step] of Object.entries(file?.markLetters ?? {})) if (letter !== "about") LETTERS.push([letter, step as keyof Material])
    noteEmberContent("marks", file && { markLetters: file.markLetters, markSets: file.markSets })
}

const mirrorRows = (rows: string[]) => rows.map(row => row.split("").reverse().join(""))
/** The same mark on the other half of the frame: LPC's facings mirror about the frame's middle. */
const mirrored = (m: Mark, side: MarkSide | undefined, frame: number): Mark => ({
    ...m,
    side,
    at: [frame - m.at[0] - m.rows[0].length, m.at[1]],
    rows: mirrorRows(m.rows),
})

const marksFor = (set: MarkSet, facing: Facing): Mark[] => {
    const from = set.mirrorFacings[facing]
    if (from) return (set.facings[from] ?? []).map(m => mirrored(m, m.side, set.frame))
    // These screen-space patterns are measured for cardinal views only. Diagonals use volume shading.
    const own: Mark[] | undefined = set.facings[facing]
    if (!own) return []
    return [...own, ...own.filter(m => m.side && set.mirrorSides[m.side]).map(m => mirrored(m, set.mirrorSides[m.side!] as MarkSide, set.frame))]
}

/** The bone a joint hangs off: a mark on the joint is dropped once that bone swings well away. */
type Joint = { [K in keyof LimbJoints]-?: LimbJoints[K] extends Vec3 ? K : never }[keyof LimbJoints]
const BONE: Record<MarkJoint, [Joint, Joint]> = {
    shoulder: ["shoulder", "elbow"],
    elbow: ["shoulder", "elbow"],
    hand: ["elbow", "hand"],
    knee: ["hip", "knee"],
    ankle: ["knee", "ankle"],
}
const armOf = (side: "L" | "R") => (side === "L" ? "armL" : "armR")
const legOf = (side: "L" | "R") => (side === "L" ? "legL" : "legR")

export const bodyStamps = (
    sk: Skeleton,
    p: Proportions,
    skin: Material,
    facing: Facing,
    o: RasterOptions = DEFAULT_RASTER,
    setName = "lpc-male",
): PixelStamp[] => {
    const set = MARK_SETS[setName]
    if (!set) return []
    const SWUNG = (set.swungLimit * Math.PI) / 180
    const TURNED = set.turnedLimit
    const FRAME = set.frame
    const yaw = rotY(FACING_YAW[facing])
    const { back } = cameraAxes(o.pitch)
    const at = (v: Vec3) => projectPoint(apply(yaw, v), o)
    const depth = (v: Vec3) => dot(apply(yaw, v), back)
    const toward = (s: Skeleton) => dot(apply(mul(yaw, s.torsoBasis), [0, 0, 1]), back)
    // Where the marks were measured: Ember standing, in this facing, at the default build.
    const standing = solvePose(restingSample(DEFAULT_PROPORTIONS, facing), DEFAULT_PROPORTIONS)
    const torsoFits = Math.abs(toward(sk) - toward(standing)) < TURNED
    // Which of the skeleton's limbs a side names, decided standing so it cannot swap mid-stride.
    const limb = (side: MarkSide | undefined, joint: MarkJoint): "L" | "R" => {
        const [l, r] = [standing.L[joint], standing.R[joint]]
        if (side === "near") return depth(l) > depth(r) ? "L" : "R"
        if (side === "far") return depth(l) > depth(r) ? "R" : "L"
        const leftmost = at(l).x < at(r).x ? "L" : "R"
        return side === "screenRight" ? (leftmost === "L" ? "R" : "L") : leftmost
    }
    const angle = (s: Skeleton, side: "L" | "R", joint: MarkJoint) => {
        const [from, to] = BONE[joint]
        const a = at(s[side][from]), b = at(s[side][to])
        return Math.atan2(b.y - a.y, b.x - a.x)
    }
    const palette: Record<string, number> = {}
    for (const [letter, step] of LETTERS) palette[letter] = skin[step]
    const stamps: PixelStamp[] = []
    for (const mark of marksFor(set, facing)) {
        let now: Vec3, then: Vec3, group: PartGroupList
        if (mark.on === "head" || mark.on === "neck" || mark.on === "chest" || mark.on === "pelvis") {
            if (!torsoFits) continue
            now = sk[mark.on]
            then = standing[mark.on]
            group = mark.on === "head" ? ["head"] : mark.on === "neck" ? ["head", "torso", "shoulder"] : ["torso", "bust", "shoulder"]
        } else {
            const side = limb(mark.side, mark.on as MarkJoint)
            let swing = Math.abs(angle(sk, side, mark.on as MarkJoint) - angle(standing, side, mark.on as MarkJoint))
            if (swing > Math.PI) swing = 2 * Math.PI - swing
            if (swing > SWUNG) continue
            now = sk[side][mark.on]
            then = standing[side][mark.on]
            group =
                mark.on === "shoulder" ? ["shoulder", "torso", armOf(side)] : mark.on === "knee" || mark.on === "ankle" ? [legOf(side)] : [armOf(side)]
        }
        const q = at(now), q0 = at(then)
        // `at` is in LPC's 64px frame; a bigger frame (a held tool's) centres the same body in it.
        const grow = [(o.width - FRAME) / 2, (o.height - FRAME) / 2]
        stamps.push({
            x: Math.round(q.x + mark.at[0] - q0.x + grow[0]),
            y: Math.round(q.y + mark.at[1] - q0.y + grow[1]),
            rows: mark.rows,
            palette,
            group,
            material: skin,
        })
    }
    return stamps
}

bindContent(catalog => (catalog.source as any).emberMarks, registerMarks)
